<template>
  <div id="app" class="min-h-screen bg-gray-50">
    <!-- Splash Screen - Always shown first -->
    <SplashScreen v-if="showSplash" :on-complete="hideSplash" />
    
    <!-- Main App Content - Hidden during splash -->
    <div v-else>
      <!-- Desktop Sidebar - Only for authenticated users -->
      <DesktopSidebar v-if="authStore.isAuthenticated" />

      <!-- Main Content Area -->
      <div class="lg:pl-64" :class="{ 'lg:pl-64': authStore.isAuthenticated }">
      <!-- PWA Install Prompt -->
      <div 
        v-if="showInstallPrompt" 
        class="fixed bottom-20 left-4 right-4 bg-blue-600 text-white rounded-lg p-4 shadow-lg z-50 md:max-w-md md:mx-auto lg:left-68"
      >
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <h3 class="font-semibold text-sm mb-1">{{ branding.appInstallTitle }}</h3>
            <p class="text-xs opacity-90">Add to home screen for better experience</p>
          </div>
          <div class="flex space-x-2 ml-4">
            <button 
              @click="installPWA" 
              class="bg-white text-blue-600 px-3 py-1 rounded text-xs font-medium hover:bg-gray-100"
            >
              Install
            </button>
            <button 
              @click="dismissInstallPrompt" 
              class="text-white opacity-75 hover:opacity-100 p-1"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      <!-- PWA Update Available -->
      <div 
        v-if="showUpdatePrompt" 
        class="fixed bottom-20 left-4 right-4 bg-green-600 text-white rounded-lg p-4 shadow-lg z-50 md:max-w-md md:mx-auto lg:left-68"
      >
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <h3 class="font-semibold text-sm mb-1">Update Available</h3>
            <p class="text-xs opacity-90">New version ready to install</p>
          </div>
          <div class="flex space-x-2 ml-4">
            <button 
              @click="updatePWA" 
              class="bg-white text-green-600 px-3 py-1 rounded text-xs font-medium hover:bg-gray-100"
            >
              Update
            </button>
            <button 
              @click="dismissUpdatePrompt" 
              class="text-white opacity-75 hover:opacity-100 p-1"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Router view for all pages -->
      <RouterView />
      
      <!-- Global loading overlay -->
      <div 
        v-if="isLoading" 
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        <div class="bg-white rounded-lg p-6 flex flex-col items-center">
          <div class="loading-spinner mb-4"></div>
          <p class="text-gray-600">{{ loadingMessage }}</p>
        </div>
      </div>
      
      <!-- Offline indicator -->
      <div 
        v-if="!isOnline" 
        class="fixed top-0 left-0 right-0 bg-red-600 text-white text-center py-2 z-40 safe-top lg:left-64"
      >
        <span class="text-sm font-medium">You're offline. Some features may not work.</span>
      </div>
      
      <!-- Sync indicator -->
      <div 
        v-if="isSyncing" 
        class="fixed top-0 left-0 right-0 bg-blue-600 text-white text-center py-2 z-40 safe-top lg:left-64"
      >
        <span class="text-sm font-medium">Syncing data...</span>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { RouterView, useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSubscriptionStore } from '@/stores/subscription'
import { useBranding } from '@/composables/useBranding'
import { subscriptionMonitor } from '@/services/subscriptionMonitor'
import DesktopSidebar from '@/components/common/DesktopSidebar.vue'
import SplashScreen from '@/views/SplashScreen.vue'

// Branding configuration
const branding = useBranding()

// Router composables
const router = useRouter()
const route = useRoute()

// Splash screen state - Skip for subscription page
const showSplash = ref(true)

// Global loading state
const isLoading = ref(false)
const loadingMessage = ref('Loading...')
const authStore = useAuthStore()
const subscriptionStore = useSubscriptionStore()

// Online/offline detection
const isOnline = ref(navigator.onLine)

// PWA states
const showInstallPrompt = ref(false)
const showUpdatePrompt = ref(false)
const isSyncing = ref(false)

// PWA event references
let deferredPrompt = null
let waitingWorker = null

// Check if we should skip splash screen for subscription page
const shouldSkipSplash = () => {
  return route.name === 'Subscription' || route.path === '/subscription'
}

// Splash screen functions
const hideSplash = () => {
  showSplash.value = false

}

// Online/offline event handlers
const handleOnline = () => {
  isOnline.value = true

  
  // Trigger background sync when back online
  if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.sync.register('background-sync-transactions')
      registration.sync.register('background-sync-products')
      isSyncing.value = true
      
      // Hide sync indicator after 3 seconds
      setTimeout(() => {
        isSyncing.value = false
      }, 3000)
    })
  }
}

const handleOffline = () => {
  isOnline.value = false

}

// PWA Install handlers
const handleBeforeInstallPrompt = (e) => {

  e.preventDefault()
  deferredPrompt = e
  
  // Don't show prompt if already dismissed
  const dismissed = localStorage.getItem('pwa-install-dismissed')
  if (!dismissed) {
    showInstallPrompt.value = true
  }
}

const installPWA = async () => {
  if (!deferredPrompt) {

    return
  }
  
  try {
    showInstallPrompt.value = false
    deferredPrompt.prompt()
    
    const { outcome } = await deferredPrompt.userChoice

    
    if (outcome === 'accepted') {

    } else {

      localStorage.setItem('pwa-install-dismissed', 'true')
    }
    
    deferredPrompt = null
  } catch (error) {
    console.error('PWA: Install error:', error)
  }
}

const dismissInstallPrompt = () => {
  showInstallPrompt.value = false
  localStorage.setItem('pwa-install-dismissed', 'true')

}

// PWA Update handlers
const handleServiceWorkerUpdate = (registration) => {
  console.log('PWA: New service worker available')
  waitingWorker = registration.waiting
  
  if (waitingWorker) {
    showUpdatePrompt.value = true
  }
  
  // Listen for controlling change
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    console.log('PWA: New service worker activated, reloading...')
    window.location.reload()
  })
}

const updatePWA = () => {
  if (!waitingWorker) {
    console.log('PWA: No waiting worker available')
    return
  }
  
  console.log('PWA: Updating to new version')
  showUpdatePrompt.value = false
  
  // Tell the waiting service worker to skip waiting
  waitingWorker.postMessage({ type: 'SKIP_WAITING' })
  waitingWorker = null
}

const dismissUpdatePrompt = () => {
  showUpdatePrompt.value = false
  waitingWorker = null
  console.log('PWA: Update prompt dismissed by user')
}

// Service Worker message handler
const handleServiceWorkerMessage = (event) => {
  const { data } = event
  
  if (data && data.type === 'SYNC_COMPLETE') {
    console.log('PWA: Background sync completed:', data.data.type)
    isSyncing.value = false
  }
}

// Register Service Worker
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      })
      
      console.log('PWA: Service worker registered successfully')
      
      // Check for updates
      registration.addEventListener('updatefound', () => {
        console.log('PWA: Service worker update found')
        handleServiceWorkerUpdate(registration)
      })
      
      // Check if there's already a waiting service worker
      if (registration.waiting) {
        handleServiceWorkerUpdate(registration)
      }
      
      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage)
      
    } catch (error) {
      console.error('PWA: Service worker registration failed:', error)
    }
  } else {
    console.log('PWA: Service workers not supported')
  }
}

// Subscription checking
const checkSubscriptionStatus = async () => {
  if (!authStore.isAuthenticated || authStore.userRole === 'superadmin') {
    subscriptionStore.resetSubscription()
    return
  }
  
  await subscriptionStore.checkSubscription()
}

// Watch for authentication changes
watch(
  () => authStore.isAuthenticated,
  async (isAuth) => {
    if (isAuth) {
      // Check subscription when user logs in
      await checkSubscriptionStatus()
      
      // Set up periodic subscription checks (every 5 minutes)
      setInterval(checkSubscriptionStatus, 5 * 60 * 1000)
    } else {
      // Reset subscription when user logs out
      subscriptionStore.resetSubscription()
    }
  },
  { immediate: true }
)

// Global loading management
const setLoading = (loading, message = 'Loading...') => {
  isLoading.value = loading
  loadingMessage.value = message
}

// Lifecycle hooks
onMounted(() => {
  // Add online/offline listeners
  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)
  
  // Add PWA install prompt listener
  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  
  // Register service worker
  registerServiceWorker()
  
  // Initialize auth store
  authStore.initializeAuth()
  
  // Initialize subscription store
  subscriptionStore.initializeSubscriptionListener()
  
  // Start subscription monitoring for authenticated users
  if (authStore.isAuthenticated && authStore.userRole !== 'superadmin') {
    console.log('Starting subscription monitoring...')
    subscriptionMonitor.start()
  }
  
  // Add global loading helper to window
  window.setGlobalLoading = setLoading
  
  // Initialize splash screen - skip for subscription page
  if (shouldSkipSplash()) {
    showSplash.value = false
    console.log('Skipping splash screen for subscription page')
  } else {
    showSplash.value = true
  }
  
  console.log(`${branding.appFullName.value} App initialized with PWA support`)
})

// Watch for route changes to handle subscription page navigation
watch(route, (newRoute) => {
  if (shouldSkipSplash()) {
    showSplash.value = false
    console.log('Route changed to subscription - hiding splash screen')
  }
}, { immediate: true })

onUnmounted(() => {
  // Remove event listeners
  window.removeEventListener('online', handleOnline)
  window.removeEventListener('offline', handleOffline)
  window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  
  if (navigator.serviceWorker) {
    navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage)
  }
  
  // Stop subscription monitoring
  subscriptionMonitor.stop()
  
  // Remove global loading helper
  delete window.setGlobalLoading
})

// Expose loading state management
defineExpose({
  setLoading,
  installPWA,
  updatePWA
})
</script>

<style scoped>
/* Component-specific styles if needed */
</style>
